/**
 * Secure Authentication Utility
 * Handles tokens, refresh logic, and secure storage
 */

const TOKEN_KEY = 'standari_access_token';
const REFRESH_KEY = 'standari_refresh_token';
const USER_KEY = 'standari_user';

// Token storage with security considerations
export const tokenStorage = {
  setTokens: (accessToken, refreshToken, user) => {
    // Use sessionStorage for access token (more secure, cleared on tab close)
    sessionStorage.setItem(TOKEN_KEY, accessToken);
    
    // Use localStorage for refresh token (persists across sessions)
    if (refreshToken) {
      localStorage.setItem(REFRESH_KEY, refreshToken);
    }
    
    // Store minimal user info
    if (user) {
      sessionStorage.setItem(USER_KEY, JSON.stringify({
        id: user.id,
        email: user.email,
        username: user.username,
        role: user.role || 'user',
        tier: user.tier || 'pilot'
      }));
    }
  },

  getAccessToken: () => {
    return sessionStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken: () => {
    return localStorage.getItem(REFRESH_KEY);
  },

  getUser: () => {
    const userStr = sessionStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  clearTokens: () => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },

  hasValidToken: () => {
    return !!sessionStorage.getItem(TOKEN_KEY);
  }
};

// API request wrapper with automatic token refresh
export const secureApi = {
  async request(url, options = {}) {
    const accessToken = tokenStorage.getAccessToken();
    
    // Add authorization header
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    let response = await fetch(url, {
      ...options,
      headers
    });
    
    // Handle 401 - try to refresh token
    if (response.status === 401 && tokenStorage.getRefreshToken()) {
      const refreshed = await this.refreshAccessToken();
      
      if (refreshed) {
        // Retry original request with new token
        headers['Authorization'] = `Bearer ${tokenStorage.getAccessToken()}`;
        response = await fetch(url, {
          ...options,
          headers
        });
      } else {
        // Refresh failed, redirect to login
        tokenStorage.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }
    }
    
    // Handle rate limiting
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After') || 60;
      throw new Error(`Rate limit exceeded. Please try again in ${retryAfter} seconds.`);
    }
    
    return response;
  },

  async refreshAccessToken() {
    const refreshToken = tokenStorage.getRefreshToken();
    
    if (!refreshToken) {
      return false;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem(TOKEN_KEY, data.access_token);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  },

  async get(url) {
    const response = await this.request(url, { method: 'GET' });
    return response.json();
  },

  async post(url, data) {
    const response = await this.request(url, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async put(url, data) {
    const response = await this.request(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return response.json();
  },

  async delete(url) {
    const response = await this.request(url, { method: 'DELETE' });
    return response.json();
  }
};

// Password strength validator (matches backend)
export const validatePasswordStrength = (password) => {
  const errors = [];
  
  if (password.length < 12) {
    errors.push('Password must be at least 12 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  const strength = Math.max(0, 5 - errors.length);
  
  return {
    isValid: errors.length === 0,
    errors,
    strength, // 0-5
    strengthLabel: ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'][strength]
  };
};

// Sanitize user input to prevent XSS
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Check if user has specific permission
export const hasPermission = (permission) => {
  const user = tokenStorage.getUser();
  if (!user) return false;
  
  const permissions = {
    admin: ['admin'],
    api_keys: ['pro', 'enterprise', 'defense'],
    advanced_export: ['enterprise', 'defense'],
    defense_features: ['defense']
  };
  
  const allowedTiers = permissions[permission] || [];
  return allowedTiers.includes(user.role) || allowedTiers.includes(user.tier);
};
